import { humanize } from '@hetivo/hetivo-lib-utils';

import type { MatrixRowGroup, MatrixRowItem } from '../types/checkbox-matrix';

/**
 * Maps a category name to the list of resource prefixes that belong to it.
 * When a permission's resource prefix appears in this map, it is grouped under
 * the category key instead of the raw resource name.
 */
export const PERMISSION_CATEGORY_GROUPS: Record<string, string[]> = {
  settings: [
    'divisions',
    'documents',
    'energy-sources',
    'energy-sources-price',
    'expense-categories',
    'expenses',
    'handling-unit-types',
    // SWOOP-378: the Inspections module's codes sit under Settings in the matrix rather than as three groups
    // of their own: Inspector (`inspections`, `repairs`) and Fleet Manager (`vehicle-inspections`).
    'inspections',
    'invitations',
    'locations',
    'pool-types',
    'repairs',
    'roles',
    'sites',
    'spaces',
    'tenant-defaults',
    'tenants',
    'territories',
    'users',
    'vehicle-groups',
    'vehicle-inspections',
    'vehicle-makes',
    'vehicle-models',
    'vehicle-ownerships',
    'vehicle-templates',
    'vehicle-types',
    'vehicles',
  ],
};

/**
 * Build a reverse lookup: resource prefix → category key.
 * Computed once at module load time.
 */
function buildResourceToCategoryMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const [category, resources] of Object.entries(PERMISSION_CATEGORY_GROUPS)) {
    for (const resource of resources) {
      map.set(resource, category);
    }
  }
  return map;
}

const RESOURCE_TO_CATEGORY = buildResourceToCategoryMap();

/**
 * Resources that should be hidden from the UI (backend-only, no corresponding page).
 * All permissions for these resources are excluded regardless of action.
 */
export const HIDDEN_RESOURCES = new Set(['permissions', 'tracking', 'unicorns', 'webhooks']);

/**
 * Transform a flat array of permission code strings into grouped MatrixRowGroup[].
 * Permission codes have the format 'resource:action' (e.g., 'users:list').
 * Resources that belong to a category group (see PERMISSION_CATEGORY_GROUPS) are
 * grouped under that category instead of the raw resource name.
 * Groups are sorted alphabetically. Items within groups are sorted by resource then action.
 */
export function transformPermissions(permissionCodes: string[]): MatrixRowGroup[] {
  const groupMap = new Map<string, MatrixRowItem[]>();

  for (const code of permissionCodes) {
    const separatorIndex = code.indexOf(':');
    if (separatorIndex === -1) continue;

    const resource = code.slice(0, separatorIndex);
    if (HIDDEN_RESOURCES.has(resource)) continue;
    const action = code.slice(separatorIndex + 1);
    const groupKey = RESOURCE_TO_CATEGORY.get(resource) ?? resource;

    // When grouped under a category, show as "List Carriers", "Manage Carriers" etc.
    const label = groupKey !== resource ? `${humanize(action)} ${humanize(resource)}` : humanize(action);

    const item: MatrixRowItem = {
      group: groupKey,
      key: code,
      label,
    };

    const existing = groupMap.get(groupKey);
    if (existing) {
      existing.push(item);
    } else {
      groupMap.set(groupKey, [item]);
    }
  }

  return [...groupMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({
      items: items.sort((a, b) => a.key.localeCompare(b.key)),
      key,
      label: humanize(key),
    }));
}
